import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../store";
import { useMsal } from "@azure/msal-react";
import Sidebar from "../../../../components/Sidebar";
import PageLoader from "../../../../components/PageLoader";
import {
  fetchInterviewReport,
  candidateAction,
  exportInterviewReport,
} from "../../../../store/interviewHostSlice";
import ActionModal from "./ActionModal";
import styles from "./InterviewReport.module.scss";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DescriptionIcon from "@mui/icons-material/Description";
import PauseIcon from "@mui/icons-material/Pause";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const InterviewReport: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const user = useSelector((state: RootState) => state.auth.user);
  const { interviewReport, reportLoading, reportError } = useSelector(
    (state: RootState) => state.interviewHost
  );

  const [actionModal, setActionModal] = useState<{
    open: boolean;
    action: "hold" | "reject" | "advance" | null;
  }>({
    open: false,
    action: null,
  });
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (candidateId) {
      dispatch(fetchInterviewReport({ candidateId }));
    }
  }, [candidateId, dispatch]);

  const handleLogout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  }, [instance]);

  const handleBackToHome = useCallback(() => {
    navigate("/agent/interview-host-agent");
  }, [navigate]);

  const handleBackToJobs = useCallback(() => {
    navigate("/agent/interview-host-agent/jobs");
  }, [navigate]);

  const handleBackToCandidates = useCallback(() => {
    if (interviewReport?.job.job_id) {
      navigate(
        `/agent/interview-host-agent/jobs/${interviewReport.job.job_id}/candidates`
      );
    }
  }, [navigate, interviewReport]);

  const handleActionClick = useCallback(
    (action: "hold" | "reject" | "advance") => {
      setActionModal({ open: true, action });
    },
    []
  );

  const handleActionConfirm = useCallback(
    async (notes: string) => {
      if (!candidateId || !actionModal.action) return;

      try {
        await dispatch(
          candidateAction({
            candidateId,
            action: actionModal.action,
            notes,
          })
        ).unwrap();

        setActionModal({ open: false, action: null });

        // Navigate back to candidates page after successful action
        if (interviewReport?.job.job_id) {
          navigate(
            `/agent/interview-host-agent/jobs/${interviewReport.job.job_id}/candidates`
          );
        }
      } catch (error) {
        console.error(`Failed to ${actionModal.action} candidate:`, error);
      }
    },
    [dispatch, candidateId, actionModal.action, navigate, interviewReport]
  );

  const handleActionModalClose = useCallback(() => {
    setActionModal({ open: false, action: null });
  }, []);

  const handleDownloadReport = useCallback(async () => {
    if (!interviewReport?.session_id) return;

    setDownloading(true);
    try {
      const blob = await dispatch(
        exportInterviewReport({ sessionId: interviewReport.session_id })
      ).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Interview_Report_${interviewReport.candidate.name.replace(/\s+/g, "_")}_${interviewReport.session_id}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download report:", error);
    } finally {
      setDownloading(false);
    }
  }, [dispatch, interviewReport]);

  const handleJumpToTimestamp = useCallback((seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play();
      setVideoPlaying(true);
    }
  }, []);

  const toggleVideoPlay = useCallback(() => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  }, [videoPlaying]);

  const handleVideoTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  const getInitials = useCallback((name: string): string => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, []);

  const getScoreColor = useCallback((score: number): string => {
    if (score >= 80) return styles.scoreHigh;
    if (score >= 60) return styles.scoreMedium;
    return styles.scoreLow;
  }, []);

  const getSeverityIcon = useCallback((severity: string) => {
    switch (severity) {
      case "positive":
        return <CheckCircleIcon className={styles.evidenceIconPositive} />;
      case "warning":
        return <WarningIcon className={styles.evidenceIconWarning} />;
      case "negative":
        return <ErrorIcon className={styles.evidenceIconNegative} />;
      default:
        return null;
    }
  }, []);

  if (reportLoading) {
    return <PageLoader label="Loading interview report..." activeNav="home" />;
  }

  if (reportError || !interviewReport) {
    return (
      <div className={styles.container}>
        <Sidebar
          activeNav="home"
          user={user || undefined}
          onLogout={handleLogout}
        />
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load interview report</h2>
            <p>{reportError || "An unexpected error occurred."}</p>
            <button
              onClick={() =>
                candidateId && dispatch(fetchInterviewReport({ candidateId }))
              }
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  const { candidate, job, overall_score, recommendation_reason } = interviewReport;

  return (
    <div className={styles.container}>
      <Sidebar
        activeNav="home"
        user={user || undefined}
        onLogout={handleLogout}
      />
      <main className={styles.mainContent}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={handleBackToHome}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <button className={styles.breadcrumbLink} onClick={handleBackToJobs}>
            Jobs
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <button
            className={styles.breadcrumbLink}
            onClick={handleBackToCandidates}
          >
            {job.title}
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>
            {candidate.name} - Report
          </span>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.avatar}>{getInitials(candidate.name)}</div>
            <div className={styles.headerInfo}>
              <h1 className={styles.candidateName}>{candidate.name}</h1>
              <p className={styles.candidateMeta}>
                {job.title} · Round 1 · {interviewReport.summary.duration_minutes}{" "}
                min · completed today · #{interviewReport.session_id}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.actionBar}>
          <button
            className={styles.btnSecondary}
            onClick={handleDownloadReport}
            disabled={downloading}
          >
            <FileDownloadIcon sx={{ fontSize: 18 }} />
            {downloading ? "Downloading..." : "Download report"}
          </button>
          <button
            className={styles.btnSecondary}
            onClick={() =>
              window.open(interviewReport.downloads.transcript_url, "_blank")
            }
          >
            <DescriptionIcon sx={{ fontSize: 18 }} />
            Transcript
          </button>
          <button
            className={styles.btnHold}
            onClick={() => handleActionClick("hold")}
          >
            <PauseIcon sx={{ fontSize: 18 }} />
            Hold
          </button>
          <button
            className={styles.btnReject}
            onClick={() => handleActionClick("reject")}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
            Reject
          </button>
          <button
            className={styles.btnAdvance}
            onClick={() => handleActionClick("advance")}
          >
            Advance to HM round
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </button>
        </div>

        {/* Score and Recommendation */}
        <div className={styles.topSection}>
          <div className={styles.scoreCircle}>
            <svg className={styles.circularProgress} viewBox="0 0 120 120">
              <circle
                className={styles.circleBackground}
                cx="60"
                cy="60"
                r="54"
              />
              <circle
                className={styles.circleProgress}
                cx="60"
                cy="60"
                r="54"
                style={{
                  strokeDasharray: `${(overall_score / 100) * 339.292} 339.292`,
                }}
              />
            </svg>
            <div className={styles.scoreText}>
              <span className={styles.scoreNumber}>{overall_score}</span>
              <span className={styles.scoreLabel}>OVERALL</span>
            </div>
          </div>

          <div className={styles.recommendationCard}>
            <div className={styles.recommendationBadge}>
              <CheckCircleIcon sx={{ fontSize: 20 }} />
              Recommendation: {interviewReport.recommendation}
            </div>
            <p className={styles.recommendationText}>{recommendation_reason}</p>
          </div>
        </div>

        {/* Main Grid */}
        <div className={styles.mainGrid}>
          {/* Left Column */}
          <div className={styles.leftColumn}>
            {/* Video Player */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>🎥</span>
                Interview recording
              </h3>
              <div className={styles.videoContainer}>
                <div className={styles.videoWrapper}>
                  <video
                    ref={videoRef}
                    className={styles.video}
                    onTimeUpdate={handleVideoTimeUpdate}
                    src={interviewReport.recording.url}
                  />
                  <button className={styles.videoPlayButton} onClick={toggleVideoPlay}>
                    {videoPlaying ? (
                      <PauseIcon sx={{ fontSize: 48 }} />
                    ) : (
                      <PlayArrowIcon sx={{ fontSize: 48 }} />
                    )}
                  </button>
                  <div className={styles.proctorBadge}>
                    <span className={styles.proctorIcon}>👁️</span>
                    Proctored session
                  </div>
                </div>
                <div className={styles.videoControls}>
                  <span className={styles.videoTime}>{formatTime(currentTime)}</span>
                  <input
                    type="range"
                    className={styles.videoSeeker}
                    min="0"
                    max={interviewReport.recording.duration_seconds}
                    value={currentTime}
                    onChange={(e) => {
                      const time = Number(e.target.value);
                      setCurrentTime(time);
                      if (videoRef.current) {
                        videoRef.current.currentTime = time;
                      }
                    }}
                  />
                  <span className={styles.videoTime}>
                    {formatTime(interviewReport.recording.duration_seconds)}
                  </span>
                </div>
                <p className={styles.videoHint}>
                  Click any evidence timestamp to jump to that answer
                </p>
              </div>
            </div>

            {/* Evidence */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>💡</span>
                Evidence-backed reasoning
              </h3>
              <div className={styles.evidenceList}>
                {interviewReport.evidence.map((item, index) => (
                  <div
                    key={index}
                    className={`${styles.evidenceItem} ${
                      styles[`evidence${item.severity.charAt(0).toUpperCase() + item.severity.slice(1)}`]
                    }`}
                  >
                    {getSeverityIcon(item.severity)}
                    <div className={styles.evidenceContent}>
                      <h4 className={styles.evidenceClaim}>{item.claim}</h4>
                      <button
                        className={styles.evidenceTimestamp}
                        onClick={() => handleJumpToTimestamp(item.timestamp_seconds)}
                      >
                        <PlayArrowIcon sx={{ fontSize: 14 }} />
                        Answer @{item.timestamp_label}
                      </button>
                      <p className={styles.evidenceExcerpt}>"{item.excerpt}"</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className={styles.rightColumn}>
            {/* Competency Scores */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>📊</span>
                Competency scores
              </h3>
              <div className={styles.competencyList}>
                {interviewReport.competency_scores.map((comp, index) => (
                  <div key={index} className={styles.competencyItem}>
                    <div className={styles.competencyHeader}>
                      <span className={styles.competencyName}>{comp.competency}</span>
                      <span className={`${styles.competencyScore} ${getScoreColor(comp.score)}`}>
                        {comp.score}
                      </span>
                    </div>
                    <div className={styles.competencyBar}>
                      <div
                        className={`${styles.competencyFill} ${getScoreColor(comp.score)}`}
                        style={{ width: `${(comp.score / comp.max_score) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Integrity & Proctoring */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>🔒</span>
                Integrity & proctoring
              </h3>
              <div className={styles.integrityList}>
                <div className={styles.integrityItem}>
                  <span className={styles.integrityLabel}>
                    <span className={styles.integrityIcon}>📧</span>
                    Email OTP verified
                  </span>
                  <span className={styles.integrityValue}>
                    {interviewReport.integrity.otp_verified ? "Yes" : "No"}
                  </span>
                </div>
                <div className={styles.integrityItem}>
                  <span className={styles.integrityLabel}>
                    <span className={styles.integrityIcon}>👤</span>
                    Single person on camera
                  </span>
                  <span className={styles.integrityValue}>
                    {interviewReport.integrity.single_person_confirmed ? "Confirmed" : "Multiple"}
                  </span>
                </div>
                <div className={styles.integrityItem}>
                  <span className={styles.integrityLabel}>
                    <span className={styles.integrityIcon}>🤖</span>
                    AI-response likelihood
                  </span>
                  <span className={styles.integrityValue}>
                    {interviewReport.integrity.ai_response_likelihood}
                  </span>
                </div>
                <div className={styles.integrityItem}>
                  <span className={styles.integrityLabel}>
                    <span className={styles.integrityIcon}>💻</span>
                    Multi-device attempt
                  </span>
                  <span className={styles.integrityValue}>
                    {interviewReport.integrity.multi_device_attempt ? "Yes" : "None"}
                  </span>
                </div>
                <div className={styles.integrityItem}>
                  <span className={styles.integrityLabel}>
                    <span className={styles.integrityIcon}>⚠️</span>
                    Focus loss (tab switch)
                  </span>
                  <span className={styles.integrityValue}>
                    {interviewReport.integrity.tab_switches} times
                  </span>
                </div>
                <div className={styles.integrityItem}>
                  <span className={styles.integrityLabel}>
                    <span className={styles.integrityIcon}>🗣️</span>
                    Speaking-speed anomaly
                  </span>
                  <span className={styles.integrityValue}>
                    {interviewReport.integrity.speaking_speed_anomaly ? "Yes" : "None"}
                  </span>
                </div>
                <div className={styles.integrityItem}>
                  <span className={styles.integrityLabel}>
                    <span className={styles.integrityIcon}>⏸️</span>
                    Suspicious pause patterns
                  </span>
                  <span className={styles.integrityValue}>
                    {interviewReport.integrity.suspicious_pause_patterns ? "Yes" : "None"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transcript */}
        <div className={styles.section}>
          <div className={styles.transcriptHeader}>
            <h3 className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>📄</span>
              Transcript
            </h3>
            <button
              className={styles.expandButton}
              onClick={() => setTranscriptExpanded(!transcriptExpanded)}
            >
              {transcriptExpanded ? (
                <>
                  Collapse
                  <ExpandLessIcon sx={{ fontSize: 20 }} />
                </>
              ) : (
                <>
                  Expand
                  <ExpandMoreIcon sx={{ fontSize: 20 }} />
                </>
              )}
            </button>
          </div>
          <div
            className={`${styles.transcriptList} ${
              transcriptExpanded ? styles.transcriptExpanded : ""
            }`}
          >
            {interviewReport.transcript.map((item, index) => (
              <div
                key={index}
                className={`${styles.transcriptItem} ${
                  item.speaker === "interviewer"
                    ? styles.transcriptInterviewer
                    : styles.transcriptCandidate
                }`}
              >
                <div className={styles.transcriptSpeaker}>
                  <span
                    className={`${styles.speakerLabel} ${
                      item.speaker === "interviewer"
                        ? styles.speakerAgent
                        : styles.speakerCandidate
                    }`}
                  >
                    {item.speaker_label}
                  </span>
                  <span className={styles.transcriptTime}>{item.timestamp_label}</span>
                </div>
                <p className={styles.transcriptText}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Action Modal */}
      {actionModal.open && actionModal.action && (
        <ActionModal
          open={actionModal.open}
          onClose={handleActionModalClose}
          onConfirm={handleActionConfirm}
          action={actionModal.action}
          candidateName={candidate.name}
        />
      )}
    </div>
  );
};

export default InterviewReport;
